import { Pager } from "@/sharedStyled";
import { Button, Flex, Table, Tooltip } from "antd";
import { useIntl } from "react-intl";
import { Date, UserDisplayName } from "@/components";
import { NumberParam, useQueryParams } from "use-query-params";
import { IProjectRisk } from "../interfaces";
import { SeverityTag } from "./SeverityTag";
import { useGetUsersByIdsMutation } from "@/services/user";
import { useEffect } from "react";
import { PrioritySeverityTag } from "./PrioritySeverityTag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faEdit, faHistory, faTrash} from "@fortawesome/free-solid-svg-icons";
import { useGetProjectRiskCategoriesQuery, useGetProjectRiskStatusesQuery } from "@/services";

interface IProps {
  data?: IProjectRisk[];
  totalCount?: number;
  isFetching: boolean;
  canUpdate: boolean;
  onUpdate: (projectRisk: IProjectRisk) => void;
  canDelete: boolean;
  onDelete: (projectRiskId: string) => void;
  onViewHistory: (projectRiskId: string) => void;
}

export const RisksTable = ({ data, totalCount, isFetching, canUpdate, onUpdate, canDelete, onDelete, onViewHistory }: IProps) => {
  const queryPrefix = 'r_';
  const [queryParams, setQueryParams] = useQueryParams({
    [queryPrefix + 'page']: NumberParam,
  });

  const intl = useIntl();

  const { data: projectRiskStatuses } = useGetProjectRiskStatusesQuery(null);
  const { data: projectRiskCategories } = useGetProjectRiskCategoriesQuery(null);

  const [getUsersByIds, { data: usersByIds }] = useGetUsersByIdsMutation();

  useEffect(() => {
    if (data && data.length > 0) {
      const ownerIds = data.map(risk => risk.ownerId).filter(Boolean);
      if (ownerIds.length > 0) {
        getUsersByIds(ownerIds);
      }
    }
  }, [data, getUsersByIds]);

  const getUserDisplay = (userId: string) => {
    if (!userId) return null;

    const userData = usersByIds?.find(({ id }: { id: string }) => id === userId);

    if (!userData) return null;

    return (
      <UserDisplayName
        displayName={userData?.displayName}
        userAccountEnabled={userData?.azureAdAccountEnabled}
        user={userData}
      />
    );
  };

  const columns = [
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.risk" }),
      dataIndex: "description",
      width: 280,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.impact" }),
      dataIndex: "impactSeverityLevel",
      render: (impact: number, record: IProjectRisk) => (
          <SeverityTag value={impact} severityLevel={record.impactSeverityLevel}/>
        ),
      width: 120,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.probability" }),
      dataIndex: "probabilitySeverityLevel",
      render: (probability: number, record: IProjectRisk) => (
          <SeverityTag value={probability} severityLevel={record.probabilitySeverityLevel}/>
        ),
      width: 120,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.priority" }),
      dataIndex: "prioritySeverityLevelValue",
      render: (priority: number, record: IProjectRisk) => (
          <PrioritySeverityTag value={priority} severityLevel={record.prioritySeverityLevel}/>
        ),
      width: 120,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.status" }),
      dataIndex: "projectRiskStatusId",
      render: (statusId: number) => {
        const statusName = projectRiskStatuses?.find(option => option.id === statusId)?.name;
        return <span>{statusName}</span>;
      },
      width: 110,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.category" }),
      dataIndex: "projectRiskCategoryId",
      render: (categoryId: number) => {
        const categoryName = projectRiskCategories?.find(category => category.id === categoryId)?.name;
        return <span>{categoryName}</span>;
      },
      width: 110,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.owner" }),
      dataIndex: "ownerId",
      render: getUserDisplay,
      width: 140,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.preventativeAction" }),
      dataIndex: "preventativeAction",
      render: (preventativeAction: string) => <Tooltip title={preventativeAction} className="line-clamp-3">{preventativeAction}</Tooltip>,
      width: 175,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.nextSteps" }),
      dataIndex: "nextSteps",
      render: (nextSteps: string) => <Tooltip title={nextSteps} className="line-clamp-3">{nextSteps}</Tooltip>, 
      width: 175,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.dateIdentified" }),
      dataIndex: "dateIdentified",
      render: (data: string) => <Date value={data} />,
      width: 130,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.target" }),
      dataIndex: "targetCompletionDate",
      render: (data: string) => <Date value={data} />,
      width: 95,
    },
    {
      title: intl.formatMessage({ id: "risksAndIssues.columns.history" }),
      dataIndex: "history",
      render: (history: string) => <Tooltip title={history} className="line-clamp-3">{history}</Tooltip>,
      width: 175,
    },
    ...((canUpdate || canDelete)
      ? [
          {
            title: intl.formatMessage({ id: "general.actions" }),
            width: 90,
            fixed: 'right' as 'right' | 'left', // Adding the 'as' assertion to resolve TypeScript type issue
            render: (_: unknown, record: IProjectRisk) => (
            <Flex>
              {canUpdate &&
                <Button
                  type="link"
                  data-test-id={`project-risk-update-${record.projectRiskPublicId}`}
                  onClick={() => onUpdate(record)}
                  icon={<FontAwesomeIcon icon={faEdit} />}
                />}
              {canDelete &&
                <Button
                  type="link"
                  data-test-id={`project-risk-delete-${record.projectRiskPublicId}`}
                  onClick={() => onDelete(record.projectRiskPublicId)}
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />}
                <Button
                    type="link"
                    data-test-id={`project-risk-history-${record.projectRiskPublicId}`}
                    onClick={() => onViewHistory(record.projectRiskPublicId)}
                    icon={<FontAwesomeIcon icon={faHistory} />}
                />
            </Flex>
          ),
          },
        ]
      : []),
  ];
  return (
    <>
      <Table
        data-test-id="project-risks-table"
        columns={columns}
        dataSource={data}
        loading={isFetching}
        rowKey={({ projectRiskPublicId }) => projectRiskPublicId}
        pagination={false}
        scroll={{ x: 1840 }}
      />
      <Pager
        current={queryParams[queryPrefix + 'page'] || undefined}
        total={totalCount}
        showSizeChanger={false}
        onChange={(page) => setQueryParams({ [queryPrefix + 'page']: page })}
        data-test-id="projects-risks-table-pagination"
      />
    </>
  );
};